<template>
  <div>
    <v-card-title class="settings-header-text d-flex justify-space-between">
      <div class="mb-2">{{ Title }}</div>
    </v-card-title>
    <v-card-subtitle>
      <v-row class="report-time-row">
        <v-col cols="12" md="7" class="pt-6 pb-0">
          Displaying {{ DisplayingText }} from
          <v-menu
            v-model="showDateSelectorMenu"
            :close-on-content-click="false"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" depressed small rounded
                >{{ displayDateName }}
                <v-icon small>mdi-chevron-down</v-icon></v-btn
              >
            </template>
            <v-sheet v-if="!showCalendar">
              <v-list
                v-for="item in timeMenuOptions"
                :key="'item-' + item.value"
              >
                <v-list-item @click="updateDateOption(item.value)">{{
                  item.name
                }}</v-list-item>
              </v-list>
            </v-sheet>

            <v-sheet
              v-if="showCalendar"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              min-width="290px"
              offset-y
            >
              <v-date-picker v-model="customRange" no-title scrollable range>
                <v-spacer></v-spacer>
                <v-btn
                  @click="updateCustomDates"
                  depressed
                  small
                  color="primary"
                >
                  Set Range
                </v-btn>
                <v-spacer></v-spacer>
              </v-date-picker>
            </v-sheet>
          </v-menu>
        </v-col>
        <v-col cols="12" md="5" class="pt-4 pb-0">
          <v-text-field
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            single-line
            label="Search"
            v-model="searchValue"
          ></v-text-field>
        </v-col>
      </v-row>
    </v-card-subtitle>
    <div class="px-4 pb-8">
      <v-data-table
        :headers="Headers"
        :loading="dataLoading"
        :items="filteredDataTableItems"
        :no-data-text="
          dataHasLoaded
            ? 'No results for these dates'
            : 'Filter and Click Run Report to Fetch Data'
        "
        :footer-props="{
          'items-per-page-options': [10, 25, 50, -1]
        }"
        class="elevation-0"
      >
        <template v-slot:item.name="{ item }">
          {{ item.name }} {{ formatPhone(item.toPhone) }}
        </template>
        <template v-slot:item.trackingNumber="{ item }">
          <div
            v-if="item.trackingNumberName != formatPhone(item.trackingNumber)"
          >
            {{ item.trackingNumberName }}
          </div>
          <div>{{ formatPhone(item.trackingNumber) }}</div>
        </template>

        <template v-slot:item.from="{ item }">
          <div v-if="item.trackingNumber == item.fromPhone">
            {{ item.trackingNumberName }}
          </div>
          <div v-else-if="item.isInbound == true">
            {{ displayCustomerName(item) }}
          </div>
          <div>{{ formatPhone(item.fromPhone) }}</div>
        </template>

        <template v-slot:item.to="{ item }">
          <div v-if="item.trackingNumber == item.toPhone && toComparison(item)">
            {{ item.trackingNumberName }}
          </div>
          <div v-else-if="item.isInbound == false">
            {{ displayCustomerName(item) }}
          </div>
          <div>{{ formatPhone(item.toPhone) }}</div>
        </template>

        <template v-slot:item.customerId="{ item }">
          <div>{{ displayCustomer(item.customerId) }}</div>
          <div>{{ item.phoneNumber }}</div>
        </template>
        <template v-slot:item.customer="{ item }">
          <v-icon
            class="customer-indicator"
            :class="{ 'customer-indicator-on': hasCustomerIndicator(item) }"
            x-small
            >mdi-circle-medium</v-icon
          >
          {{ displayCustomerName(item) }}<br />{{
            formatPhone(
              item.phoneNumber
                ? item.phoneNumber
                : item.trackingNumber == item.toPhone
                ? item.fromPhone
                : item.toPhone
            )
          }}
        </template>
        <template v-slot:item.callIcon="{ item }">
          <v-tooltip left>
            <template v-slot:activator="{ on }">
              <v-icon small v-on="on">{{
                item.isInbound ? "mdi-phone-incoming" : "mdi-phone-outgoing"
              }}</v-icon>
            </template>
            <span>{{ item.isInbound ? "Incoming" : "Outgoing" }}</span>
          </v-tooltip>
        </template>

        <template v-slot:item.recording="{ item }">
          <v-bottom-sheet inset @input="onSheetOpenClose(item, $event)">
            <template v-slot:activator="{ on: sheet, attrs }">
              <v-tooltip left v-if="item.recordingExternalId">
                <template v-slot:activator="{ on: tooltip }">
                  <div v-on="{ ...sheet, ...tooltip, attrs }">
                    <v-btn icon>
                      <v-icon small>{{
                        item.isCallRecording
                          ? "mdi-record-rec"
                          : "mdi-voicemail"
                      }}</v-icon>
                      <v-icon small>mdi-play</v-icon>
                    </v-btn>
                  </div>
                </template>
                <span>{{
                  item.isCallRecording ? "Call Recording" : "Voicemail"
                }}</span>
              </v-tooltip>
            </template>
            <v-card tile>
              <v-progress-linear
                :value="0"
                class="my-0"
                height="3"
              ></v-progress-linear>
              <v-list>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>{{
                      displayCustomer(item.customerId)
                    }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ formatDateDisplay(item.startTime) }}
                      <v-icon small>{{
                        item.isCallRecording
                          ? "mdi-record-rec"
                          : "mdi-voicemail"
                      }}</v-icon>
                      <!-- {{ item.isCallRecording ? "Call Recording" : "Voicemail"}} -->
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-spacer></v-spacer>
                  <!-- <audio
                    controls
                    :id="recordingId(item)"
                    :src="recordingUrl(item)"
                    @play="setVMStatus('play')"
                    @pause="setVMStatus('pause')"
                    @ended="setVMStatus('ended')"
                    autoplay
                    class="recording-controls"
                  ></audio> -->
                  <CallAudioPlayer
                    :callAudioId="recordingId(item)"
                    :accountExternalId="accountExternalId"
                    :audioPath="recordingPath(item)"
                    :setPlayStatusFunction="setVMStatus"
                  ></CallAudioPlayer>
                </v-list-item>
              </v-list>
            </v-card>
          </v-bottom-sheet>
        </template>

        <template v-slot:item.createdAt="{ item }">
          {{ formatDateDisplay(item.createdAt) }}
        </template>
        <template v-slot:item.startTime="{ item }">
          {{ formatDateDisplay(item.startTime) }}
        </template>
        <template v-slot:item.lastDate="{ item }">
          {{ formatDateDisplay(item.lastDate) }}
        </template>
        <template v-slot:item.durationSeconds="{ item }">
          {{ callRecordingTiming(item.durationSeconds) }}
        </template>
        <template v-slot:item.maxDuration="{ item }">
          {{ callRecordingTiming(item.maxDuration) }}
        </template>
        <template v-slot:item.avgDuration="{ item }">
          {{ callRecordingTiming(item.avgDuration) }}
        </template>
        <template v-slot:item.fromPhone="{ item }">
          {{ formatPhone(item.fromPhone) }}
        </template>
        <template v-slot:item.toPhone="{ item }">
          {{ formatPhone(item.toPhone) }}
        </template>
        <template v-slot:item.isInbound="{ item }">
          {{ `${item.isInbound ? "Inbound" : "Outbound"}` }}
        </template>
        <template v-slot:item.action="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon
                small
                v-on="on"
                @click="
                  item.customerId > 0
                    ? openCustomerDetails(item)
                    : openContactMergeModal(item)
                "
                >{{
                  item.customerId > 0
                    ? "mdi-open-in-new"
                    : "mdi-account-multiple-plus"
                }}</v-icon
              >
            </template>
            <span>{{
              item.customerId > 0 ? "View Customer" : "Add/Merge Customer"
            }}</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from "../../../globalVars";
import axios from "axios";
import { add, startOfDay, startOfMonth, startOfYear, format } from "date-fns";
import { USPhoneNumberDisplayFormat } from "../../../helpers/phoneNumberFormatter";
import Fuse from "fuse.js";
import { CUSTOMER_SEARCH_FUSE_OPTIONS } from "../../../globalVars";
import CallAudioPlayer from "../../calls/CallAudioPlayer.vue";

export default {
  name: "GraphAndGrid",
  components: {
    CallAudioPlayer
  },
  props: {
    Title: String,
    DisplayingText: String,
    Headers: Array,
    ApiPath: String,
    SearchLocal: Boolean,
    IsReport: {
      type: Boolean,
      default: true
    },
    GetParamType: String,
    GetParamName: String,
    GetParamSearchName: String
  },
  data: function() {
    return {
      dataTableItems: [],
      dataLoading: false,
      dataHasLoaded: false,
      showDateSelectorMenu: false,
      showCalendar: false,
      customStart: this.formatDatePickerDate(new Date()),
      customEnd: this.formatDatePickerDate(new Date()),
      customRange: [],
      timeMenu: "prevMonth",
      timeMenuOptions: [
        {
          name: "Custom",
          value: "custom"
        },
        {
          name: "Today",
          value: "today"
        },
        {
          name: "Yesterday",
          value: "yesterday"
        },
        {
          name: "Prev Week",
          value: "prevWeek"
        },
        {
          name: "Prev Month",
          value: "prevMonth"
        },
        {
          name: "This Month",
          value: "thisMonth"
        },
        {
          name: "Last Month",
          value: "lastMonth"
        },
        {
          name: "This Year",
          value: "thisYear"
        },
        {
          name: "Last Year",
          value: "lastYear"
        }
      ],
      searchValue: null,
      searchDelayTimer: null,

      callVMStatus: "new",
      showVMControls: false,
      callVmId: ""
    };
  },
  computed: {
    settings() {
      return this.$store.getters["entities/settings/query"]().first();
    },
    accountExternalId() {
      return this.$store.getters["entities/accounts/query"]().first()
        .externalId;
    },
    displayDateName() {
      let vueThis = this;
      const selectedObj = this.timeMenuOptions.filter(val => {
        return val.value == vueThis.timeMenu;
      })[0];
      if (selectedObj.value == "custom" && this.customStart && this.customEnd) {
        return `${this.formatDate(this.customStart)} - ${this.formatDate(
          this.customEnd
        )}`;
      } else {
        return selectedObj.name;
      }
    },
    filteredDataTableItems() {
      let options = Object.assign({}, CUSTOMER_SEARCH_FUSE_OPTIONS);
      options.keys = [
        "trackingNumberName",
        // "companyName",
        // "firstName",
        // "lastName",
        "toPhone",
        "fromPhone",
        "phoneNumber",
        "messageBody",
        "message"
      ];
      if (this.SearchLocal && this.searchValue) {
        let fuse = new Fuse(this.dataTableItems, options);
        return fuse.search(this.searchValue);
      } else {
        return this.dataTableItems;
      }
    },
    customerLookup() {
      return this.$store.getters["entities/customerLookups/query"]()
        .orderBy("date", "desc")
        .get();
    },
    userId() {
      return this.$store.getters["entities/settings/query"]().first().userId;
    },
    user() {
      return this.$store.getters["entities/users/find"](this.userId);
    },
    userPreferences() {
      let results = this.$store.getters[
        "entities/userPreferences/query"
      ]().first();
      return results;
    },
    reportPreferences() {
      let results =
        this.userPreferences && this.userPreferences.telecomPreferences
          ? this.userPreferences.reportPreferences
          : {};
      return results;
    }
  },
  async created() {
    if (
      this.reportPreferences &&
      this.reportPreferences.timeMenu &&
      this.reportPreferences.customStart
    ) {
      this.timeMenu = this.reportPreferences.timeMenu;
      this.customStart = new Date(this.reportPreferences.customStart);
      this.customEnd = new Date(this.reportPreferences.customEnd);
      this.customRange = [
        this.formatDatePickerDate(this.customStart),
        this.formatDatePickerDate(this.customEnd)
      ];
      this.runReport();
    } else {
      this.updateDateOption(this.timeMenu);
      this.runReport();
    }
  },
  methods: {
    hasCustomerIndicator(item) {
      // debugger;
      if (item.customerId > 0) {
        // debugger;
        const customers = this.customerLookup
          ? this.customerLookup.filter(function(val) {
              return item.customerId == val.id;
            })
          : null;
        let customer = null;
        if (customers && customers.length > 0) {
          customer = customers[0];
        }
        if (
          customer &&
          customer.date &&
          new Date(customer.date) < new Date(item.lastDate)
        ) {
          return true;
        } else if (customer && !customer.date) {
          return true;
        } else {
          return false;
        }
      } else {
        return true;
      }
    },
    openContactMergeModal(item) {
      let newNumber =
        item.customerId < 0 ? -(item.customerId + 10000000000) + "" : null;
      let vueThis = this;
      let timer = setInterval(function checkCustomerEditorModals() {
        vueThis.$root.$children[0].showCustomerEditorModals = true;
        if (
          vueThis.$root.$children[0].$refs["customer-editor-modals"] !=
          undefined
        ) {
          clearInterval(timer);
          let theContainer =
            vueThis.$root.$children[0].$refs["customer-editor-modals"];
          if (item.trackingNumber) {
            const phoneNumber =
              item.trackingNumber == item.toPhone
                ? item.fromPhone
                : item.trackingNumber == item.fromPhone
                ? item.toPhone
                : null;
            theContainer.ContactInfoDetails = USPhoneNumberDisplayFormat(
              phoneNumber
            );
          } else {
            theContainer.ContactInfoDetails = newNumber;
          }
          if (item.trackingNumberId) {
            theContainer.CallTrackingNumberSource = item.trackingNumberId;
          }
          if (item.sourceId) {
            theContainer.CustomerSource = parseInt(item.sourceId);
          }
          theContainer.CallbackRecord = item;
          theContainer.RerunReportCallback =
            vueThis.updateCustomerIdOnMergedRecord;
          theContainer.showContactMergeModal = true;
        }
      }, 100);
    },
    updateCustomerIdOnMergedRecord(record, customerId) {
      const total = this.dataTableItems.length;
      for (let i = 0; i < total; i++) {
        const tempItem = this.dataTableItems[i];
        if (
          (tempItem.isInbound == true &&
            tempItem.fromPhone == record.fromPhone) ||
          (tempItem.isInbound == false && tempItem.toPhone == record.toPhone) ||
          tempItem.phoneNumber == record.phoneNumber
        ) {
          this.dataTableItems[i].customerId = customerId;
        }
      }
    },
    openCustomerDetails(item) {
      const customerId = item.customerId;
      this.$store.dispatch("entities/customers/clickCustomer", {
        customerId: customerId
      });
      this.$store.dispatch("entities/users/update", {
        where: this.user.id,
        data: { currentCustomerId: customerId }
      });
      this.$root.$children[0].showCustomerEditorModals = false;
      let vueThis = this;
      this.$nextTick(() => {
        vueThis.$router.push({ name: "customers" });
      });
    },
    onSheetOpenClose(item, val) {
      if (val == false) {
        const sounds = document.getElementsByTagName("audio");
        for (let i = 0; i < sounds.length; i++) {
          sounds[i].pause();
          sounds[i].currentTime = 0;
        }
      } else {
        const tempId = this.recordingId(item);
        setTimeout(function() {
          document.getElementById(tempId).play();
        }, 100);
      }
    },
    callRecordingTiming(duration) {
      if (duration) {
        duration = parseInt(duration);
      }
      let secondTiming =
        duration % 60 < 10 ? "0" + (duration % 60) : duration % 60;
      let minuteTiming =
        Math.floor(duration / 60) < 10
          ? "0" + Math.floor(duration / 60)
          : Math.floor(duration / 60);
      return minuteTiming + ":" + secondTiming;
    },
    recordingId(call) {
      return "recording-" + call.recordingExternalId;
    },
    recordingPath(call) {
      return `call-recording/${call.recordingExternalId}.mp3`;
    },
    setVMStatus(status) {
      this.callVMStatus = status;
    },
    updateFormattedDate(d) {
      const baseDate = new Date(d);
      return add(baseDate, {
        minutes: baseDate.getTimezoneOffset()
      }); // Needed because of TimeZone changing the dates
      // return baseDate;
    },
    updateDateOption(selected) {
      if (selected == "custom") {
        const baseDate = new Date();
        this.customStart = startOfDay(
          new Date(
            add(baseDate, {
              minutes: baseDate.getTimezoneOffset(),
              days: -8
            })
          )
        );
        this.customEnd = startOfDay(
          new Date(
            add(baseDate, {
              minutes: baseDate.getTimezoneOffset(),
              days: -1
            })
          )
        );
        this.customRange = [
          this.formatDatePickerDate(this.customStart),
          this.formatDatePickerDate(this.customEnd)
        ];
        this.showCalendar = true;
      } else {
        if (selected == "today") {
          const baseDate = new Date();
          this.customStart = startOfDay(
            new Date(
              add(baseDate, {
                minutes: baseDate.getTimezoneOffset(),
                days: 0
              })
            )
          );
          this.customEnd = startOfDay(
            new Date(
              add(baseDate, {
                minutes: baseDate.getTimezoneOffset(),
                days: 1
              })
            )
          );
        } else if (selected == "yesterday") {
          const baseDate = new Date();
          this.customStart = startOfDay(
            new Date(
              add(baseDate, {
                minutes: baseDate.getTimezoneOffset(),
                days: -1
              })
            )
          );
          this.customEnd = startOfDay(
            new Date(
              add(baseDate, {
                minutes: baseDate.getTimezoneOffset(),
                days: 0
              })
            )
          );
        } else if (selected == "prevWeek") {
          const baseDate = new Date();
          this.customStart = startOfDay(
            new Date(
              add(baseDate, {
                minutes: baseDate.getTimezoneOffset(),
                weeks: -1
              })
            )
          );
          this.customEnd = startOfDay(
            new Date(
              add(baseDate, {
                minutes: baseDate.getTimezoneOffset(),
                days: 1
              })
            )
          );
        } else if (selected == "prevMonth") {
          const baseDate = new Date();
          this.customStart = startOfDay(
            new Date(
              add(baseDate, {
                minutes: baseDate.getTimezoneOffset(),
                months: -1
              })
            )
          );
          this.customEnd = startOfDay(
            new Date(
              add(baseDate, {
                minutes: baseDate.getTimezoneOffset(),
                days: 1
              })
            )
          );
        } else if (selected == "thisMonth") {
          const baseDate = new Date();
          this.customStart = startOfDay(
            new Date(
              startOfMonth(
                new Date(
                  add(baseDate, {
                    minutes: baseDate.getTimezoneOffset(),
                    days: 0
                  })
                )
              )
            )
          );
          this.customEnd = startOfDay(
            new Date(
              startOfMonth(
                new Date(
                  add(baseDate, {
                    minutes: baseDate.getTimezoneOffset(),
                    months: 1
                  })
                )
              )
            )
          );
        } else if (selected == "lastMonth") {
          const baseDate = new Date();
          this.customStart = startOfDay(
            new Date(
              startOfMonth(
                new Date(
                  add(baseDate, {
                    minutes: baseDate.getTimezoneOffset(),
                    months: -1
                  })
                )
              )
            )
          );
          this.customEnd = startOfDay(
            new Date(
              startOfMonth(
                new Date(
                  add(baseDate, {
                    minutes: baseDate.getTimezoneOffset(),
                    days: 0
                  })
                )
              )
            )
          );
        } else if (selected == "lastYear") {
          const baseDate = new Date();
          this.customStart = startOfDay(
            new Date(
              startOfYear(
                new Date(
                  add(baseDate, {
                    minutes: baseDate.getTimezoneOffset(),
                    years: -1
                  })
                )
              )
            )
          );
          this.customEnd = startOfDay(
            new Date(
              startOfYear(
                new Date(
                  add(baseDate, {
                    minutes: baseDate.getTimezoneOffset(),
                    days: 0
                  })
                )
              )
            )
          );
        } else if (selected == "thisYear") {
          const baseDate = new Date();
          this.customStart = startOfDay(
            new Date(
              startOfYear(
                new Date(
                  add(baseDate, {
                    minutes: baseDate.getTimezoneOffset(),
                    days: 0
                  })
                )
              )
            )
          );
          this.customEnd = startOfDay(
            new Date(
              startOfYear(
                new Date(
                  add(baseDate, {
                    minutes: baseDate.getTimezoneOffset(),
                    years: 1
                  })
                )
              )
            )
          );
        }
        this.$store.dispatch("entities/userPreferences/updateUserPreferences", {
          id: this.userPreferences ? this.userPreferences.id : null,
          accountId: this.user.accountId,
          userId: this.user.id,
          reportPreferences: {
            timeMenu: selected,
            customStart: this.customStart,
            customEnd: this.customEnd
          }
        });
        this.showDateSelectorMenu = false;
        this.searchValue = null;
        this.runReport();
      }
      this.timeMenu = selected; // show the selected time
    },
    updateCustomDates() {
      const dateRange = this.customRange;
      this.customStart = this.updateFormattedDate(dateRange[0]);
      this.customEnd = this.updateFormattedDate(dateRange[1]);
      this.showDateSelectorMenu = false;
      this.$store.dispatch("entities/userPreferences/updateUserPreferences", {
        id: this.userPreferences.id,
        accountId: this.user.accountId,
        userId: this.user.id,
        reportPreferences: {
          timeMenu: "custom",
          customStart: this.customStart,
          customEnd: this.customEnd
        }
      });
      this.runReport();
    },
    displayCustomerPhone(id) {
      if (id > 0) {
        let customer = this.customerLookup.filter(val => {
          return val.id == id;
        });
        const phones = customer && customer.length ? customer[0].phone : null;
        if (phones) {
          return phones[0].number;
        }
      }
      return null;
    },
    displayCustomer(id) {
      if (id > 0) {
        let customer = this.customerLookup.filter(val => {
          return val.id == id;
        });
        return customer && customer.length ? customer[0].name : null;
      }
      return null;
    },
    displayCustomerName(item) {
      if (item.customerId > 0) {
        if (
          item.companyName != null ||
          item.firstName != null ||
          item.lastName != null
        ) {
          return item.companyName
            ? item.companyName
            : `${[
                item.firstName ? item.firstName : "",
                item.lastName ? item.lastName : ""
              ].join(" ")}`;
        } else {
          const customer = this.customerLookup
            ? this.customerLookup.filter(function(val) {
                return item.customerId == val.id;
              })
            : null;
          return customer && customer.length > 0 ? customer[0].name : "No Name";
        }
      }
      return "New Customer";
    },
    displayFormat(d) {
      const selectedObj = this.timeMenuOptions.filter(val => {
        return val.value == d;
      })[0];
      if (selectedObj.value == "custom" && this.customStart && this.customEnd) {
        return `${this.customStart} - ${this.customEnd}`;
      } else {
        return selectedObj.name;
      }
    },
    formatDate(d) {
      return format(new Date(d), "M/d/y");
    },
    formatDateDisplay(d) {
      return format(new Date(d), "M/d/y h:mm a");
    },
    formatPhone(phone) {
      return USPhoneNumberDisplayFormat(phone);
    },
    formatDatePickerDate(d) {
      return format(new Date(d), "y-MM-dd");
    },
    runReport() {
      this.dataLoading = true;
      if (this.IsReport) {
        this.fetchReportData();
      } else {
        this.fetchRecordData();
      }
      //updateTable
    },
    fetchRecordData() {
      let dataTableItems = [];
      const startTime = new Date(this.customStart) || new Date();
      const endBase = this.customEnd || new Date();
      const endTime = new Date(
        add(endBase, {
          days: 1
        })
      );
      axios({
        method: "get",
        url: API_BASE_URL + this.ApiPath,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`
        },
        params: {
          // q: this.searchValue,
          "createdAt[$gte]": startTime.toISOString(),
          "createdAt[$lt]": endTime.toISOString()
        }
      })
        .then(response => {
          const data = response.data.data;
          dataTableItems = data;
          this.dataTableItems = dataTableItems;
          this.dataLoading = false;
          this.dataHasLoaded = true;
        })
        .catch(error => {
          console.error("Failed to get report", error);
        });
    },
    fetchReportData() {
      this.dataLoading = true;
      let dataTableItems = [];
      const startTime = new Date(this.customStart) || new Date();
      const endBase = this.customEnd || new Date();
      const endTime = new Date(
        add(endBase, {
          days: 1
        })
      );
      axios({
        method: "get",
        url: API_BASE_URL + this.ApiPath,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`
        },
        params: {
          type: this.GetParamType,
          name:
            this.searchValue == null
              ? this.GetParamName
              : this.GetParamSearchName,
          q: this.searchValue,
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString()
        }
      })
        .then(response => {
          const data = response.data.data;
          dataTableItems = data;
          this.dataTableItems = dataTableItems;
          this.dataLoading = false;
          this.dataHasLoaded = true;
        })
        .catch(error => {
          console.error("Failed to get report", error);
        });
    },
    toComparison(item) {
      return (
        ("" + item.trackingNumberName).trim() !=
        ("" + this.formatPhone(item.toPhone)).trim()
      );
    }
  },
  watch: {
    showDateSelectorMenu: function(after, before) {
      before;
      if (after == false) {
        this.showCalendar = false;
      }
    },
    searchValue: function(after, before) {
      after;
      before;
      if (!this.SearchLocal) {
        let vueThis = this;
        clearTimeout(this.searchDelayTimer);
        this.searchDelayTimer = setTimeout(function() {
          vueThis.searchDelayTimer = null;
          vueThis.runReport();
        }, 1200);
      }
    }
  }
};
</script>

<style scoped lang="stylus">
div.spacer {
  width: 3rem;
}
div.settings-card > div, div.settings-card > div > div > div {
  padding-left: 0;
  padding-right: 0;
}
.recording-controls-min{
}
.recording-controls-min audio{
  width: 0;
}
i.customer-indicator {
  margin-left: -1rem;
  color: transparent;
}
i.customer-indicator-on {
  margin-left: -1rem;
  color: #ff5252;
}
</style>
